import cx from "classnames";
import type Fuse from "fuse.js";
import { type FC, Fragment, useState } from "react";

import { safeStringify } from "@moment/api-collab";
import { AddFilledIcon, ChevronDownIcon, MisuseIcon } from "@moment/icons";
import { Chip, DropdownMenu, InputText, MomentButton, stringToColorVariant } from "@moment/ui";

import { BasicDialog, useDialog } from "../../Dialog";
import { type DataGridColumnDefTag } from "../useColumnState";
import { useEditableValue } from "../useEditableValue";
import type { TableCellValueCommonProps } from "./types";
import { markHighlights } from "./utils";

type TagValue = string[] | string | undefined;

export interface TableCellValueTagProps extends TableCellValueCommonProps {
	columnTypeInfo: DataGridColumnDefTag<unknown, unknown>["fieldTypeInfo"];
	highlightIndices?: Fuse.FuseResultMatch["indices"];
	renderAsRowDetail?: boolean;
}

export const TableCellValueTag: FC<TableCellValueTagProps> = ({
	value: inputValue,
	columnTypeInfo,
	defaultValue,
	highlightIndices,
	onChange,
	isEditable,
	renderAsRowDetail,
}) => {
	const [newTagValue, setNewTagValue] = useState("");
	const addTagDialog = useDialog();
	const { value, handleChange, handleBlur } = useEditableValue<TagValue>(
		"text",
		isEditable,
		inputValue ?? "",
		defaultValue,
		onChange
	);

	if (!Array.isArray(value) && typeof value !== "string" && value !== undefined) {
		return (
			<div className="flex gap-1 rounded bg-negative-mid px-2 py-0.5 text-sm text-secondary">
				<div className="mt-0.5 flex">
					<MisuseIcon />
				</div>
				Tag must be an array of strings
			</div>
		);
	}

	const deleteAllTags = () => {
		handleChange([]);
	};

	const deleteTag = (tag: string) => {
		if (value === undefined) {
			return;
		}

		if (!Array.isArray(value)) {
			handleChange([]);
			return;
		}

		const newValue = value.filter((v) => v !== tag);
		handleChange(newValue);
	};
	const replaceTag = (tag: string) => {
		handleChange([tag]);
	};
	const addTag = (tag: string) => {
		const newValue = [...(value ?? []), tag];
		handleChange(newValue);
	};
	const handleAddTagConfirm = () => {
		addTag(newTagValue);
		setNewTagValue("");
	};

	const handleAddTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewTagValue(e.target.value);
	};
	const renderTagValue = (v: unknown, i = 0) => {
		if (typeof v !== "string") {
			const stringified = safeStringify(value);
			return <Fragment key={`${stringified}-${i}`}>{stringified}</Fragment>;
		}

		const foundVariant = columnTypeInfo?.tags.find((tag) => tag.label === v);
		const colorVariant = foundVariant ? foundVariant.color : stringToColorVariant(v);

		return (
			<Chip
				level="secondary"
				fontVariant="no-caps"
				onDismiss={() => deleteTag(v)}
				colorVariant={colorVariant}
				size="md"
			>
				{markHighlights(v, highlightIndices)}
			</Chip>
		);
	};

	const chevron = (
		<ChevronDownIcon
			className={cx("ml-auto shrink-0", {
				"!fill-icon-disabled": !isEditable,
				"fill-icon-default": isEditable,
			})}
		/>
	);

	const buttonContent = value ? (
		<>
			<div className="flex w-full min-w-0 items-center gap-2">
				{Array.isArray(value) ? value.map(renderTagValue) : <>{renderTagValue(value)}</>}
				<BasicDialog
					isOpen={addTagDialog.isOpen}
					closeDialog={addTagDialog.closeDialog}
					title="Add tag"
					titleIcon={<AddFilledIcon />}
					onCancel={addTagDialog.closeDialog}
					onConfirm={handleAddTagConfirm}
					confirmText="Add Tag"
					content={
						<InputText
							fullWidth
							value={newTagValue}
							size="sm"
							onChange={handleAddTagChange}
						/>
					}
				/>
			</div>
			{chevron}
		</>
	) : (
		chevron
	);

	const content = (
		<DropdownMenu.Content className="min-w-[12rem]" align="start">
			<DropdownMenu.Item
				className="text-secondary"
				onSelect={() => {
					deleteAllTags();
				}}
			>
				Blank
			</DropdownMenu.Item>
			{columnTypeInfo?.tags.map(({ label, color }, i) => (
				<DropdownMenu.Item key={`${label}-${i}`} onSelect={() => replaceTag(label)}>
					<Chip level="secondary" fontVariant="no-caps" colorVariant={color} size="md">
						{label}
					</Chip>
				</DropdownMenu.Item>
			))}
			{/* FIXME: Re-add this back! */}
			{/* <DropdownMenu.Separator />
			<DropdownMenu.Item onSelect={() => addTagDialog.openDialog()}>
				<Add />
				<div>Add New</div>
			</DropdownMenu.Item> */}
		</DropdownMenu.Content>
	);

	const editableContent = (
		<div
			className={cx("flex flex-col", {
				"w-full": !renderAsRowDetail,
				"w-48": renderAsRowDetail,
			})}
		>
			<DropdownMenu.Root
				onOpenChange={(open) => {
					if (!open) {
						handleBlur();
					}
				}}
			>
				<DropdownMenu.Trigger disabled={!isEditable} asChild>
					<MomentButton
						level={renderAsRowDetail ? "secondary" : "quaternary"}
						className={cx("h-9 !no-underline", { "-m-2": !renderAsRowDetail })}
					>
						{buttonContent}
					</MomentButton>
				</DropdownMenu.Trigger>
				{renderAsRowDetail ? <DropdownMenu.Portal>{content}</DropdownMenu.Portal> : content}
			</DropdownMenu.Root>
		</div>
	);

	return isEditable ? editableContent : buttonContent;
};
